import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { Heart, BookOpen, ArrowLeft, Users, Sparkles, X, Filter, ChevronDown } from 'lucide-react';
import { Button } from '../components/ui/button';
import { AuthContext } from '../App';
import axios from 'axios';
import { getLevel } from '@/utils/readingLevels';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Multi-select dropdown component
const MultiSelectDropdown = ({ label, options, selected, onChange, nightMode }) => {
  const [isOpen, setIsOpen] = useState(false);

  const toggleOption = (option) => {
    if (selected.includes(option)) {
      onChange(selected.filter(s => s !== option));
    } else {
      onChange([...selected, option]);
    }
  };

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 rounded-full text-sm border transition-colors"
        style={{
          background: nightMode ? 'var(--bg-secondary)' : 'white',
          borderColor: selected.length > 0 ? 'var(--accent-2)' : (nightMode ? 'var(--sketch-border)' : '#e5e7eb'),
          color: 'var(--text-primary)'
        }}
      >
        <span>{label}</span>
        {selected.length > 0 && (
          <span 
            className="px-1.5 py-0.5 rounded-full text-xs"
            style={{ background: 'var(--accent-2)', color: 'white' }}
          >
            {selected.length}
          </span>
        )}
        <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      
      {isOpen && (
        <>
          <div 
            className="fixed inset-0 z-10" 
            onClick={() => setIsOpen(false)}
          />
          <div 
            className="absolute top-full left-0 mt-1 z-20 min-w-48 max-h-60 overflow-y-auto rounded-lg shadow-lg border"
            style={{
              background: nightMode ? 'var(--bg-secondary)' : 'white',
              borderColor: nightMode ? 'var(--sketch-border)' : '#e5e7eb'
            }}
          >
            {options.length === 0 ? (
              <div className="px-3 py-2 text-sm text-center" style={{ color: 'var(--text-secondary)' }}>
                No options available
              </div>
            ) : options.map((option) => {
              const value = typeof option === 'object' ? option.value : option;
              const displayLabel = typeof option === 'object' ? option.label : option;
              const isSelected = selected.includes(value);
              
              return (
                <button
                  key={value}
                  onClick={() => toggleOption(value)}
                  className="w-full px-3 py-2 text-left text-sm flex items-center gap-2 hover:bg-opacity-50"
                  style={{
                    background: isSelected ? 'var(--accent-1)' : 'transparent',
                    color: 'var(--text-primary)'
                  }}
                >
                  <span 
                    className={`w-4 h-4 rounded border flex items-center justify-center text-xs`}
                    style={{
                      borderColor: isSelected ? 'var(--accent-2)' : 'var(--text-secondary)',
                      background: isSelected ? 'var(--accent-2)' : 'transparent',
                      color: isSelected ? 'white' : 'transparent'
                    }}
                  >
                    {isSelected && '✓'}
                  </span>
                  <span className="capitalize">{displayLabel}</span>
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

const CommunityPage = () => {
  const navigate = useNavigate();
  const { user, nightMode } = useContext(AuthContext);
  const isGuest = !user || user.id === 'guest-user';
  const [stories, setStories] = useState([]);
  const [filteredStories, setFilteredStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [likedStories, setLikedStories] = useState({});
  const [likingInProgress, setLikingInProgress] = useState({});
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const limit = 40;
  
  // Filter states
  const [selectedTopics, setSelectedTopics] = useState([]);
  const [selectedStyles, setSelectedStyles] = useState([]);
  const [selectedAges, setSelectedAges] = useState([]);

  // Extract unique filter options from stories
  const availableTopics = [...new Set(stories.map(s => s.topic).filter(Boolean))].sort();
  const availableStyles = [...new Set(stories.flatMap(s => s.story_styles || []).filter(Boolean))].sort();
  const availableAges = [...new Set(stories.map(s => getLevel(s.reading_level)).filter(Boolean))].sort();

  useEffect(() => {
    fetchCommunityStories(false);
  }, []);

  // Apply filters whenever stories or filter selections change
  useEffect(() => {
    let result = [...stories];
    
    if (selectedTopics.length > 0) {
      result = result.filter(story => 
        selectedTopics.some(topic => 
          story.topic?.toLowerCase().includes(topic.toLowerCase())
        )
      );
    }
    
    if (selectedStyles.length > 0) {
      result = result.filter(story => 
        story.story_styles?.some(style => 
          selectedStyles.includes(style)
        )
      );
    }
    
    if (selectedAges.length > 0) {
      result = result.filter(story => 
        selectedAges.includes(getLevel(story.reading_level))
      );
    }
    
    setFilteredStories(result);
  }, [stories, selectedTopics, selectedStyles, selectedAges]);

  const fetchCommunityStories = async (loadMore = false) => {
    try {
      if (loadMore) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }
      
      const currentSkip = loadMore ? stories.length : 0;
      const response = await axios.get(`${API}/community/stories?skip=${currentSkip}&limit=${limit}`);
      
      const newStories = response.data.stories;
      const totalCount = response.data.total;
      
      if (loadMore) {
        setStories(prev => [...prev, ...newStories]);
      } else {
        setStories(newStories);
      }
      
      setTotal(totalCount);
      setHasMore(currentSkip + newStories.length < totalCount);

      // Check like status for each story if user is logged in
      if (user && user.id !== 'guest-user') {
        const token = localStorage.getItem('token');
        for (const story of newStories) {
          try {
            const likeResponse = await axios.get(`${API}/stories/${story.id}/like-status`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            setLikedStories(prev => ({
              ...prev,
              [story.id]: likeResponse.data.has_liked
            }));
          } catch (err) {
            console.error('Failed to get like status:', err);
          }
        }
      }
    } catch (err) {
      console.error('Failed to fetch community stories:', err);
      setError('Failed to load community stories');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const handleLike = async (storyId) => {
    if (!user || user.id === 'guest-user') {
      navigate('/?login=true');
      return;
    }

    if (likingInProgress[storyId]) return;

    setLikingInProgress(prev => ({ ...prev, [storyId]: true }));
    const token = localStorage.getItem('token');
    const isLiked = likedStories[storyId];

    try {
      if (isLiked) {
        await axios.delete(`${API}/stories/${storyId}/like`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setLikedStories(prev => ({ ...prev, [storyId]: false }));
        setStories(prev => prev.map(s => 
          s.id === storyId ? { ...s, like_count: Math.max(0, (s.like_count || 0) - 1) } : s
        ));
      } else {
        await axios.post(`${API}/stories/${storyId}/like`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setLikedStories(prev => ({ ...prev, [storyId]: true }));
        setStories(prev => prev.map(s => 
          s.id === storyId ? { ...s, like_count: (s.like_count || 0) + 1 } : s
        ));
      }
    } catch (err) {
      console.error('Failed to toggle like:', err);
      if (err.response?.status === 401) {
        navigate('/?login=true');
      }
    } finally {
      setLikingInProgress(prev => ({ ...prev, [storyId]: false }));
    }
  };

  const handleReadStory = (storyId) => {
    navigate(`/community/story/${storyId}`);
  };

  const clearAllFilters = () => {
    setSelectedTopics([]);
    setSelectedStyles([]);
    setSelectedAges([]);
  };

  const hasActiveFilters = selectedTopics.length > 0 || selectedStyles.length > 0 || selectedAges.length > 0;

  if (loading && stories.length === 0) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${nightMode ? 'night-gradient' : 'playful-gradient'}`}>
        <div className="text-center">
          <Users className="w-16 h-16 mx-auto mb-4 animate-pulse" style={{ color: 'var(--accent-2)' }} />
          <p className="text-2xl font-bold" style={{ fontFamily: 'Fredoka, sans-serif', color: 'var(--text-primary)' }}>
            Loading community stories...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${nightMode ? 'night-gradient' : 'playful-gradient'} page-enter`}>
      {/* Header */}
      <header className="p-6 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            onClick={() => navigate('/')}
            className="rounded-full w-20"
          >
            <ArrowLeft className="w-6 h-6" style={{ color: 'var(--text-primary)' }} />
          </Button>
          <div className="flex items-center gap-3">
            <Users className="w-10 h-10" style={{ color: 'var(--accent-2)' }} strokeWidth={2.5} />
            <h1 className="text-2xl font-bold" style={{ fontFamily: 'Fredoka, sans-serif', color: 'var(--text-primary)' }}>
              Community Stories
            </h1>
          </div>
        </div>
        {!user && (
          <Button
            onClick={() => navigate('/?login=true')}
            className="pill-button"
            style={{ background: 'var(--accent-2)', color: 'white' }}
          >
            Sign in to upvote
          </Button>
        )}
      </header>

      {/* Description */}
      <div className="container mx-auto px-6 mb-6">
          <p className="text-xl" style={{ color: 'var(--text-secondary)' }}>
            Discover amazing stories created by our community! 
            {!user && ' Sign in to upvote your favorites.'}
          </p>
      </div>

      {/* Filters */}
      <div className="container mx-auto px-6 mb-6">
        <div className="flex flex-wrap items-center gap-3">
          <Filter className="w-5 h-5" style={{ color: 'var(--text-secondary)' }} />
          
          <MultiSelectDropdown
            label="Topic"
            options={availableTopics}
            selected={selectedTopics}
            onChange={setSelectedTopics}
            nightMode={nightMode}
          />
          
          <MultiSelectDropdown
            label="Style"
            options={availableStyles}
            selected={selectedStyles}
            onChange={setSelectedStyles}
            nightMode={nightMode}
          />
          
          <MultiSelectDropdown
            label="Reading Level"
            options={availableAges}
            selected={selectedAges}
            onChange={setSelectedAges}
            nightMode={nightMode}
          />
          
          {hasActiveFilters && (
            <Button
              variant="ghost"
              size="sm"
              onClick={clearAllFilters}
              className="flex items-center gap-1 text-sm"
              style={{ color: 'var(--accent-2)' }}
            >
              <X className="w-4 h-4" />
              Clear All
            </Button>
          )}
        </div>
        
        {hasActiveFilters && (
          <p className="mt-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
            Showing {filteredStories.length} of {stories.length} stories
          </p>
        )}
      </div>

      {/* Stories Grid */}
      <div className="container mx-auto px-6 pb-12">
        {error && (
          <div className="text-center py-12">
            <p className="text-red-500">{error}</p>
            <Button onClick={() => fetchCommunityStories()} className="mt-4">
              Try Again
            </Button>
          </div>
        )}

        {!error && filteredStories.length === 0 && (
          <div className="text-center py-12">
            <BookOpen className="w-16 h-16 mx-auto mb-4" style={{ color: 'var(--text-secondary)' }} />
            <h2 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Fredoka, sans-serif', color: 'var(--text-primary)' }}>
              {hasActiveFilters ? 'No Stories Match Filters' : 'No Stories Yet'}
            </h2>
            <p style={{ color: 'var(--text-secondary)' }}>
              {hasActiveFilters ? 'Try adjusting your filters.' : 'Check back soon for community stories!'}
            </p>
            {hasActiveFilters && (
              <Button onClick={clearAllFilters} className="mt-4" variant="outline">
                Clear Filters
              </Button>
            )}
          </div>
        )}

        {filteredStories.length > 0 && (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2 sm:gap-4">
              {filteredStories.map((story) => (
              <div
                key={story.id}
                className="story-card overflow-hidden hover:scale-102 transition-transform cursor-pointer"
                onClick={() => handleReadStory(story.id)}
              >
                {/* Story Image - Using thumbnail for faster loading */}
                {(
                  story.thumbnail ? (
                    <div className="aspect-video w-full overflow-hidden">
                      <img
                        src={`data:image/png;base64,${story.thumbnail}`}
                        alt={story.title}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    </div>
                  ) : (
                    <div 
                      className="aspect-video w-full flex items-center justify-center"
                      style={{ background: 'var(--accent-1)' }}
                    >
                      <BookOpen className="w-8 h-8 sm:w-12 sm:h-12" style={{ color: 'var(--accent-2)' }} />
                    </div>
                  )
                )}

                {/* Story Info */}
                <div className="p-1 sm:p-0.5">
                  <h3 
                    className="font-bold text-sm sm:text-lg mb-0.5 line-clamp-2 leading-tight"
                    style={{ fontFamily: 'Fredoka, sans-serif', color: 'var(--text-primary)' }}
                  >
                    {story.title}
                  </h3>

                  {/* Summary */}
                  {story.summary ? (
                    <p 
                      className="text-xs mb-1 line-clamp-4 leading-snug"
                      style={{ color: 'var(--text-secondary)' }}
                    >
                      {story.summary}
                    </p>
                  ) : null }
                  
                  {/* Combined Metadata Flow */}
                  <div className="flex flex-wrap gap-1 mb-0.5 text-[10px] sm:text-xs">
                    {/* Reading Level */}
                    <span 
                      className="px-1.5 py-0.5 rounded-full font-medium whitespace-nowrap capitalize"
                      style={{ 
                        background: nightMode ? 'var(--accent-1)' : 'var(--accent-2)', 
                        color: nightMode ? 'white' : 'var(--text-secondary)'
                      }}
                    >
                      {getLevel(story.reading_level)}
                    </span>

                    {/* Topic */}
                    {story.topic && (
                      <span 
                        className="px-1.5 py-0.5 rounded-full border truncate max-w-[100px]"
                        style={{ borderColor: 'var(--sketch-border)', color: 'var(--text-secondary)' }}
                      >
                        {story.topic}
                      </span>
                    )}

                    {/* Styles */}
                    {story.story_styles && story.story_styles.length > 0 && (
                      story.story_styles.slice(0, 2).map((style, idx) => (
                        <span 
                          key={idx}
                          className="px-1.5 py-0.5 rounded-full capitalize whitespace-nowrap"
                          style={{ 
                            background: nightMode ? 'var(--accent-3)' : 'var(--accent-1)',
                            color: nightMode ? 'white' : 'var(--text-secondary)'
                          }}
                        >
                          {style}
                        </span>
                      ))
                    )}
                  </div>

                  <div className="flex items-center justify-end">
                    {/* Like Button */}
                    <Button
                      variant="ghost"
                      size="sm"
                      className="flex items-center gap-1 h-6 px-2 -mr-2 hover:bg-transparent"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleLike(story.id);
                      }}
                      disabled={likingInProgress[story.id]}
                    >
                      <Heart
                        className={`w-3.5 h-3.5 sm:w-5 sm:h-5 transition-colors ${
                          likedStories[story.id] ? 'fill-red-500 text-red-500' : ''
                        }`}
                        style={{ color: likedStories[story.id] ? '#ef4444' : 'var(--text-secondary)' }}
                      />
                      <span className="text-xs sm:text-sm" style={{ color: 'var(--text-secondary)' }}>
                        {story.like_count || 0}
                      </span>
                    </Button>
                  </div>
 
                </div>
              </div>
              ))}
            </div>
            
            {/* Load More Button */}
            {hasMore && !hasActiveFilters && (
              <div className="text-center mt-8">
                <Button
                  onClick={() => fetchCommunityStories(true)}
                  disabled={loadingMore}
                  className="pill-button"
                  style={{ background: 'var(--accent-3)', color: 'white' }}
                >
                  {loadingMore ? 'Loading...' : `Load More (${stories.length} of ${total})`}
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default CommunityPage;
